import { useParams, useNavigate, Link } from "react-router-dom";
import { useEffect, useState } from "react";
import axios from "axios";
import Sidebar from "../../../components/sidebar/Sidebar";
import LoadingSpinner from "../../../components/LoadingSpinner";
import "../../accounts/view/ViewAccountPage.css";
import { Money, Username } from "../../../utils/privacyMask";
import { PAYMENT_ICONS, PAYMENT_COLORS } from "../../../utils/paymentMethods";

function InfoItem({ icon, label, value, accent }) {
  return (
    <div className="info-item">
      <div className="info-label"><i className={`bi ${icon}`} />{label}</div>
      <div className={`info-value ${!value ? 'muted' : ''}`} style={accent ? { color: accent, fontWeight: 600 } : {}}>
        {value || 'Not provided'}
      </div>
    </div>
  );
}

export default function ViewTransactionPage() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [transaction, setTransaction] = useState(null);
  const [account, setAccount]         = useState(null);
  const [loading, setLoading]         = useState(true);
  const [error, setError]             = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };
        const txRes = await axios.get(`${process.env.REACT_APP_API_URL}/transaction/${id}`, { headers });
        setTransaction(txRes.data);
        if (txRes.data.accountID) {
          const accRes = await axios.get(`${process.env.REACT_APP_API_URL}/account/${txRes.data.accountID}`, { headers });
          setAccount(accRes.data);
        }
        setLoading(false);
      } catch (err) {
        setError(err.response?.data?.message || "Failed to fetch transaction");
        setLoading(false);
      }
    };
    fetchData();
  }, [id]);

  if (loading) return <LoadingSpinner />;
  if (error || !transaction) return (
    <div className="view-page">
      <div className="view-panel">
        <div style={{ padding: 24, color: '#f87171' }}>
          <i className="bi bi-exclamation-triangle-fill me-2" />
          {error || 'Transaction not found'}
        </div>
      </div>
    </div>
  );

  const pm = transaction.paymentMethod;

  return (
    <>
      <Sidebar title="View Transaction" />

      <div className="view-page">

        {/* ── Header ── */}
        <div className="view-header">
          <div className="view-header-left">
            <div className="view-avatar" style={{ background: 'linear-gradient(135deg, #06b6d4, #3b82f6)' }}>
              <i className="bi bi-receipt" />
            </div>
            <div>
              <div className="view-title">Transaction Details</div>
              <div className="view-subtitle">
                ID: {transaction._id}
                {account && <> · <Username value={account.account.username} /></>}
              </div>
            </div>
          </div>
          <Link to={`/transaction/edit/${transaction._id}`} className="btn-edit-account">
            <i className="bi bi-pencil-square" /> Edit Transaction
          </Link>
        </div>

        {/* ── Stat cards ── */}
        <div className="view-stats">
          <div className="view-stat-card" style={{ '--accent': '#10b981', '--glow': 'rgba(16,185,129,0.3)' }}>
            <div className="view-stat-label">Revenue</div>
            <div className="view-stat-value"><Money value={transaction.revenue} /></div>
            <i className="bi bi-cash-stack view-stat-icon" />
          </div>
          <div className="view-stat-card" style={{ '--accent': '#3b82f6', '--glow': 'rgba(59,130,246,0.3)' }}>
            <div className="view-stat-label">Profit</div>
            <div className="view-stat-value"><Money value={transaction.profit} /></div>
            <i className="bi bi-graph-up-arrow view-stat-icon" />
          </div>
          <div className="view-stat-card" style={{ '--accent': PAYMENT_COLORS[pm] || '#94a3b8', '--glow': 'rgba(148,163,184,0.2)' }}>
            <div className="view-stat-label">Payment Method</div>
            <div className="view-stat-value" style={{ fontSize: '1.2rem', textTransform: 'capitalize' }}>
              {pm || '—'}
            </div>
            <i className={`bi ${PAYMENT_ICONS[pm] || 'bi-dash-circle'} view-stat-icon`} />
          </div>
          <div className="view-stat-card" style={{ '--accent': '#06b6d4', '--glow': 'rgba(6,182,212,0.3)' }}>
            <div className="view-stat-label">Date</div>
            <div className="view-stat-value" style={{ fontSize: '1.1rem' }}>
              {new Date(Number(transaction.transactionDate)).toLocaleDateString()}
            </div>
            <i className="bi bi-calendar-event view-stat-icon" />
          </div>
        </div>

        {/* ── Transaction details ── */}
        <div className="view-panel">
          <div className="view-panel-header">
            <h6 className="view-panel-title">
              <i className="bi bi-receipt" style={{ color: '#06b6d4' }} />
              Transaction Information
            </h6>
          </div>
          <div className="info-grid">
            <InfoItem icon="bi-person-fill"     label="Account"         value={account?.account?.username ? <Username value={account.account.username} /> : null} />
            <InfoItem icon="bi-calendar-event"  label="Date"            value={new Date(Number(transaction.transactionDate)).toLocaleDateString()} />
            <InfoItem icon="bi-cash-stack"      label="Revenue"         value={<Money value={transaction.revenue} />}  accent="#10b981" />
            <InfoItem icon="bi-graph-up-arrow"  label="Profit"          value={<Money value={transaction.profit} />}   accent="#3b82f6" />
            <InfoItem icon="bi-credit-card"     label="Payment Method"  value={pm ? pm.charAt(0).toUpperCase() + pm.slice(1) : null} />
            <InfoItem icon="bi-journal-text"    label="Notes"           value={transaction.notes} />
          </div>
        </div>

        {/* ── Actions ── */}
        <div style={{ display: 'flex', gap: 12, marginTop: 4 }}>
          <button className="btn-edit-account" onClick={() => window.history.back()}>
            <i className="bi bi-arrow-left" /> Back
          </button>
          {account && (
            <button className="btn-edit-account" onClick={() => navigate(`/account/view/${account.account._id}`)}>
              <i className="bi bi-person-fill" /> View Account
            </button>
          )}
        </div>

      </div>
    </>
  );
}
